import 'styled-components/macro';

import {
  ContextBubble,
  ContextBubbleAnimation,
  ContextBubbleBackground,
} from '@homex/se-react-components';
import moment from 'moment-timezone';
import { useRef } from 'react';
import { useSelector } from 'react-redux';

import avatarPlaceholder from 'assets/avatar-placeholder.svg';
import mailboxSvg from 'assets/mailbox.svg';
import { CarouselBackButton } from 'components/CarouselBackButton';
import { Phrase } from 'components/Phrase';
import { RemoteImage } from 'components/RemoteImage';
import { ReactComponent as CalendarSvg } from 'components/svg/Calendar.svg';
import { ReactComponent as ClockSvg } from 'components/svg/Clock.svg';
import { ReactComponent as InputErrorSvg } from 'components/svg/InputError.svg';
import { ReactComponent as SummaryMobilePhoneSvg } from 'components/svg/SummaryMobilePhone.svg';
import { DateTimeFormat, IState } from 'typings';

import {
  Heading,
  Name,
  SalesPerson,
  SummaryDetails,
  SummaryItem,
  SummaryItems,
  Title,
  errorTooltipStyles,
  faceStyles,
  navTooltipStyles,
  summaryItemImageStyles,
} from './styles';

const userTimezone = moment.tz.guess();

export const CarouselItemSalesSummaryTop = () => {
  const {
    addressTimezone = '',
    customerPhone,
    serviceZoneTimezone,
    street1,
    timeSlot: { contact, endTime, isUnavailable, label, startTime } = {
      contact: null,
      endTime: '',
      isUnavailable: false,
      label: null,
      startTime: '',
    },
    viewportSize,
  } = useSelector((state: IState) => ({
    addressTimezone: state.userInput.address.timezone,
    customerPhone: state.userInput.customerPhone,
    serviceZoneTimezone: state.userInput.serviceZoneTimezone,
    street1: state.userInput.address.street1,
    timeSlot: state.userInput.selectedTimeSlot,
    viewportSize: state.device.viewportSize,
  }));

  const customerTooltipRef = useRef<ContextBubble | null>(null);
  const scheduleTooltipRef = useRef<ContextBubble | null>(null);

  const avatarMediaId = contact?.avatarMediaId || '';
  const firstName = contact?.firstName || '';
  const lastName = contact?.lastName || '';
  const title = contact?.title || '';

  const contactFullName = `${firstName} ${lastName}`;

  const endTimeString = moment
    .tz(endTime, addressTimezone)
    .format(DateTimeFormat.TIME_END);
  const startDate = moment.tz(startTime, addressTimezone);
  const startTimeString = startDate.format(DateTimeFormat.TIME_START);
  const timezoneAbbreviation = moment.tz(addressTimezone).zoneAbbr();
  const isTimezoneVisible =
    addressTimezone !== serviceZoneTimezone || addressTimezone !== userTimezone;

  const dateString = startDate.format(DateTimeFormat.DATE_HEADER);
  const timeString =
    label ||
    `${startTimeString}-${endTimeString} ${
      isTimezoneVisible ? timezoneAbbreviation : ''
    }`;

  const handleScheduleInfoClick = () => {
    if (scheduleTooltipRef.current) {
      scheduleTooltipRef.current.doNextAnimation();

      if (customerTooltipRef.current) {
        customerTooltipRef.current.close();
      }
    }
  };

  const handleCustomerInfoClick = () => {
    if (customerTooltipRef.current) {
      customerTooltipRef.current.doNextAnimation();

      if (scheduleTooltipRef.current) {
        scheduleTooltipRef.current.close();
      }
    }
  };

  return (
    <>
      <CarouselBackButton />
      <Heading>
        <Phrase id="SALES_SUMMARY.HEADER" />
      </Heading>
      <SummaryDetails>
        <SummaryItems>
          {contact && (
            <SalesPerson>
              {avatarMediaId ? (
                <RemoteImage
                  alt={contactFullName}
                  css={faceStyles}
                  mediaId={avatarMediaId}
                />
              ) : (
                <img
                  alt={contactFullName}
                  css={faceStyles}
                  src={avatarPlaceholder}
                />
              )}
              <span>
                <Name>{contactFullName}</Name>
                <Title>{title}</Title>
              </span>
            </SalesPerson>
          )}
          <SummaryItem onClick={handleScheduleInfoClick}>
            <CalendarSvg css={summaryItemImageStyles} />
            <span>{dateString}</span>
          </SummaryItem>
          <SummaryItem as="span" onClick={handleScheduleInfoClick}>
            {isUnavailable ? (
              <>
                <ContextBubble
                  background={ContextBubbleBackground.DANGER}
                  css={errorTooltipStyles}
                  isEnabled={isUnavailable}
                  pointer={{
                    horizontal: viewportSize === 'large' ? 153 : 92,
                    vertical: 'bottom',
                  }}
                >
                  <Phrase id="SALES_SUMMARY.ERROR" />
                </ContextBubble>
                <InputErrorSvg css={summaryItemImageStyles} />
              </>
            ) : (
              <ClockSvg css={summaryItemImageStyles} />
            )}
            <span>{timeString}</span>
          </SummaryItem>
          <ContextBubble
            animation={ContextBubbleAnimation.FADE_IN_OUT}
            css={navTooltipStyles}
            isClosableByX={true}
            isOpenByDefault={false}
            pointer={{
              horizontal: 'center',
              vertical: 'top',
            }}
            reclickBehavior="toggle"
            ref={(contextBubble) =>
              (scheduleTooltipRef.current = contextBubble)
            }
          >
            <Phrase id="SALES_SUMMARY.NAV.SCHEDULE.TOOLTIP.HTML" />
          </ContextBubble>
          <ContextBubble
            animation={ContextBubbleAnimation.FADE_IN_OUT}
            css={navTooltipStyles}
            isClosableByX={true}
            isOpenByDefault={false}
            pointer={{
              anchor: 'left',
              horizontal: ['large', 'small'].includes(viewportSize) ? 71 : 88,
              vertical: 'top',
            }}
            reclickBehavior="toggle"
            ref={(contextBubble) =>
              (customerTooltipRef.current = contextBubble)
            }
          >
            <Phrase id="SALES_SUMMARY.NAV.CUSTOMER.TOOLTIP.HTML" />
          </ContextBubble>
          <SummaryItem onClick={handleCustomerInfoClick}>
            <img alt="" css={summaryItemImageStyles} src={mailboxSvg} />
            <span>{street1}</span>
          </SummaryItem>
          <SummaryItem onClick={handleCustomerInfoClick}>
            <SummaryMobilePhoneSvg css={summaryItemImageStyles} />
            <span>{customerPhone.number}</span>
          </SummaryItem>
        </SummaryItems>
      </SummaryDetails>
    </>
  );
};
